import { Circle, Verified } from "@mui/icons-material";
import { Badge, Box, Paper, Rating, styled, Typography } from "@mui/material";
import { Stack } from "@mui/system";
import React from "react";
import useFormattedImageUrl from "../../Utils/useFormattedImageUrl";
import { tokens } from "../../store/tokens";

const ProfileCard = (props) => {
  const { profileDetails, fetchedReview, commesFrom } = props;
  const { cname, cslogan } = profileDetails;
  const { total_review } = fetchedReview;
  const formattedLogo = useFormattedImageUrl(profileDetails?.agency_logo);
  const formattedPic = useFormattedImageUrl(profileDetails.profile_pic);

  // Swap in your own presence source when you have one — hardcoding this
  // true tells every visitor the profile is online, which isn't accurate.
  const isOnline = Boolean(profileDetails?.is_online);

  return (
    <Paper
      elevation={0}
      sx={{
        padding: { xs: 4, md: 6 },
        position: "relative",
        overflow: "hidden",
        borderRadius: 0, // this sits full-bleed above a rounded card below it
        background: tokens.banner.primary.background,
      }}
    >
      <Stack
        direction={"row"}
        justifyContent="space-between"
        alignItems={"center"}
        spacing={3}
        sx={{ minHeight: { md: "22vh", xs: "26vh" } }}
      >
        <Stack
          direction={"row"}
          justifyContent={{ xs: "center", md: "flex-end" }}
          alignItems={"center"}
          sx={{ width: { xs: "34%", md: "44%" } }}
        >
          <StyledBadge badgeContent={"Top Rated"}>
            <Box
              component="img"
              sx={{
                height: 100,
                width: 100,
                objectFit: "cover",
                cursor: "pointer",
                borderRadius: "50%",
                border: `3px solid ${tokens.banner.primary.text}`,
              }}
              alt={cname ?? "Profile photo"}
              src={
                commesFrom === "personal"
                  ? formattedPic ?? formattedLogo
                  : formattedLogo ?? formattedPic
              }
            />
          </StyledBadge>
        </Stack>

        <Stack sx={{ width: { xs: "65%", md: "55%" } }} spacing={0.5}>
          <Stack
            direction={"row"}
            spacing={0.75}
            alignItems={"center"}
            sx={{
              alignSelf: "flex-start",
              px: 1.25,
              py: 0.25,
              borderRadius: `${tokens.radius.pill}px`,
              border: `1px solid ${
                isOnline ? tokens.color.success : "rgba(255,255,255,0.4)"
              }`,
            }}
          >
            <Circle
              sx={{
                fontSize: 8,
                color: isOnline ? tokens.color.success : "rgba(255,255,255,0.5)",
              }}
            />
            <Typography
              variant="caption"
              sx={{
                fontWeight: 700,
                color: isOnline ? tokens.color.success : tokens.banner.primary.textMuted,
              }}
            >
              {isOnline ? "Online" : "Offline"}
            </Typography>
          </Stack>

          <Stack direction={"row"} spacing={0.5} alignItems={"center"}>
            <Typography
              variant="h5"
              sx={{ fontWeight: 700, color: tokens.banner.primary.text }}
            >
              {cname}
            </Typography>
            <Verified sx={{ fontSize: 18, color: tokens.color.success }} />
          </Stack>

          {cslogan && (
            <Typography
              variant="body2"
              sx={{ color: tokens.banner.primary.textMuted }}
            >
              {cslogan}
            </Typography>
          )}

          <Stack direction={"row"} spacing={1} alignItems={"center"}>
            <Rating
              name="read-only"
              value={4.5}
              precision={0.5}
              readOnly
              sx={{
                fontSize: "1.1rem",
                "& .MuiRating-iconFilled": { color: tokens.textOn.dark.brand },
                "& .MuiRating-iconEmpty": { color: "rgba(255,255,255,0.35)" },
              }}
            />
            <Typography
              variant="body2"
              sx={{ color: tokens.banner.primary.textMuted }}
            >
              ({total_review ?? 0} reviews)
            </Typography>
          </Stack>
        </Stack>
      </Stack>
    </Paper>
  );
};

export default ProfileCard;

const StyledBadge = styled(Badge)(() => ({
  "& .MuiBadge-badge": {
    right: 19,
    top: 75,
    textAlign: "center",
    fontSize: "0.6rem",
    fontWeight: 700,
    color: tokens.color.white,
    width: 40,
    height: 40,
    border: `3px solid ${tokens.color.teal}`,
    backgroundColor: tokens.color.tealDark,
    padding: 10,
    borderRadius: "50%",
    // `color` on <Badge> only accepts MUI palette keys, not arbitrary
    // strings like "green" — this override is the actual source of the
    // badge's color, so no `color` prop is needed on the component itself.
  },
}));